// WS0b (ARCA-87) — ThemeProvider: provides the single static take-two theme
// descriptor to the in-canvas atlas / rune-token consumers.
//
// The multi-theme runtime swap is gone (board directive: "don't keep theming").
// There is no $themeKey store, no setTheme, and no applyTheme: the identity CSS
// vars are declared statically at :root (theme.css), and this descriptor exists
// only so the atlas loader (LetterLayer) and the rune-token particle palette
// (EffectLayer) share one source of truth for the canonical theme's sprite /
// rune data.
//
// On mount we load the one canonical manifest once. If it fails the chrome still
// renders (identity tokens are static CSS); descriptor-dependent in-canvas
// embellishments simply fall back to their defaults.

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { DEFAULT_THEME, type ThemeDescriptor } from './descriptor'
import { loadTheme } from './load'

interface ThemeContextValue {
  descriptor: ThemeDescriptor | null
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [descriptor, setDescriptor] = useState<ThemeDescriptor | null>(null)

  useEffect(() => {
    let cancelled = false
    loadTheme(DEFAULT_THEME)
      .then((d) => {
        if (!cancelled) setDescriptor(d)
      })
      .catch((err) => {
        if (cancelled) return
        // The canonical manifest is bundled-active (served from public/themes),
        // so this only fires on a genuine asset/CSP failure. Log + degrade.
        // eslint-disable-next-line no-console
        console.error('[runegambit] theme descriptor load failed:', err)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ descriptor }}>
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * Read the static theme descriptor from any descendant.
 * Throws if used outside `<ThemeProvider>` (catches integration mistakes during
 * development).
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>')
  }
  return ctx
}
